import React, { useEffect, useMemo, useState } from "react";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { RequestHandlingInstructionType } from "../../../common/service/server/Types";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import {
  DoNotContactReason,
  RequestHandlingInstructionDto,
} from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import {
  applyDataRecipientFilters,
  useDataRecipientFilter,
} from "../../components/molecules/vendor/DataRecipientFilter";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import {
  usePlatformAppsByVendorKey,
  useRequestInstructions,
} from "../../hooks/useOrganizationVendors";
import { alphabetically } from "../../surveys/util";
import { getConsumerVendors } from "../../util/resources/vendorUtils";
import { shouldNotContactForDelete } from "../../util/requestInstructions";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import {
  Button,
  ExplanatoryCard,
  Modal,
  RadioGroup,
  SimpleCheckbox,
  Slideout,
  TextInput,
  Toggle,
  ToolTip,
  useSnackBar,
} from "@truevault/aquilo";
import { BoltIcon, BoltSlashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Form } from "react-final-form";
import { RefreshFn, useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { StepNumberCircle } from "./WorkflowSetupTabs";
import { CustomMessageSampleDto } from "../../../common/service/server/dto/CustomMessageSampleDto";
import { UpdateVendorArgs } from "../data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import "./vendor-instructions.css";
import { isThirdPartyRecipient, SHOPIFY_APP_VENDOR_ID } from "../../util/vendors";
import clsx from "clsx";
import { Routes } from "../../root/routes";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import { PrivacyCenterIntegrationsDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { useHistory } from "react-router";
import { NetworkRequest } from "../../../common/models";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PageLoading } from "../../components";
import { eligibleVendorsToContactForDeleteRequest } from "../../components/dataRequest/stayCompliant/steps/delete/ContactVendors";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { useManagedAdNetworks } from "../../../common/hooks/dataRecipientSettingsHooks";
import {
  canVendorHaveDataDeleted,
  vendorsForDeleteInfoStep,
} from "../../components/dataRequest/stayCompliant/steps/delete/DeleteInfo";
import { parseMultiselectAnswerWithNoneOption } from "../../surveys/survey";
import { OrganizationManagedAdNetworkDto } from "../../../common/service/server/dto/OrganizationManagedAdNetworkDto";

const requestType = "DELETE" as RequestHandlingInstructionType;

export const DeleteInstructionsPage = () => {
  const history = useHistory();
  const snackbar = useSnackBar();

  const [organization, orgRequest, refreshOrg] = usePrimaryOrganization();

  // Auxiliary data needed to configure delete workflows

  const { data: dataMap, request: dataMapRequest } = useDataMap(organization.id);

  const { answers: sellingAndSharingAnswers } = useSurvey(
    organization.id,
    SELLING_AND_SHARING_SURVEY_NAME,
  );

  const { managedAdNetworks } = useManagedAdNetworks(organization.id);

  const { settings: apiIntegrations } = useIntegrationsSettings();

  // Data fetching and local state for Shopify installed status

  const [shopifyPlatformApps, , refreshShopifyPlatformApps] = usePlatformAppsByVendorKey(
    organization?.id,
    "shopify",
    /* onlyUseSettled = */ true,
  );
  const showShopifyColumn = shopifyPlatformApps !== null;

  const [shopifyInstalledVendorIds, setShopifyInstalledVendorIds] = useState<string[] | null>(null);
  useEffect(() => {
    if (shopifyPlatformApps && shopifyInstalledVendorIds === null) {
      setShopifyInstalledVendorIds(shopifyPlatformApps.installed);
    }
  }, [shopifyPlatformApps, shopifyInstalledVendorIds, setShopifyInstalledVendorIds]);

  // Data fetching and local state for instructions

  const [instructions, setInstructions] = useState<null | RequestHandlingInstructionDto[]>(null);

  const [, instructionsRequest, fetchInstructions] = useRequestInstructions(
    organization.id,
    requestType,
    (data: RequestHandlingInstructionDto[]) => {
      setInstructions(data);
    },
    /* onlyUseSettled = */ true,
  );

  // Data fetching and local state for vendors

  const [allVendors, vendorsRequest, fetchVendors] = useOrganizationVendors(
    organization.id,
    undefined,
    true,
  );

  const filters = useDataRecipientFilter();
  const filteredVendors = useMemo(
    () => applyDataRecipientFilters(allVendors, filters.props.filters),
    [allVendors, filters],
  );

  // Get the selected custom audience vendor managers from the survey
  const customAudienceVendorManagers = parseMultiselectAnswerWithNoneOption(
    sellingAndSharingAnswers,
    "custom-audience-feature-separate-vendor",
  );

  // Filter out managed networks from showing as separate vendors (similar to OptOutInstructionsPage)
  const vendors = useMemo(() => {
    if (!filteredVendors || !managedAdNetworks || !customAudienceVendorManagers) {
      return filteredVendors;
    }

    // Get all network IDs that are managed by someone (only if the manager is still selected)
    const managedAdNetworksIds: string[] = [
      ...new Set(
        managedAdNetworks
          ?.filter((item) => customAudienceVendorManagers.includes(item.managerId))
          ?.map((item) => item.networkId) || [],
      ),
    ];

    // Get all unique manager IDs
    const managementVendorIds: string[] = [
      ...new Set(managedAdNetworks?.map((item) => item.managerId) || []),
    ];

    // Filter management vendors to only include those that are still selected in the survey
    const managementVendors =
      filteredVendors?.filter(
        (v) => managementVendorIds.includes(v.id) && customAudienceVendorManagers.includes(v.id),
      ) || [];

    let result = filteredVendors?.filter((v) => {
      // If a management vendor exists, then hide vendors that are managed by the management vendor
      if (managementVendors.length > 0 && managedAdNetworksIds.includes(v.id)) {
        return false;
      }
      return true;
    });

    // Add management vendors that should appear in delete instructions
    if (managementVendors.length > 0 && managedAdNetworksIds.length > 0) {
      const managementVendorsToAdd = managementVendors.filter((vendor) => {
        // Skip if already in vendors list
        if (result?.find((v) => v.id === vendor.id)) {
          return false;
        }
        return true;
      });

      if (managementVendorsToAdd.length > 0) {
        result?.push(...managementVendorsToAdd);
      }
    }

    return result;
  }, [filteredVendors, managedAdNetworks, customAudienceVendorManagers]);

  const sortedVendors = vendors?.sort(alphabetically)?.filter((v) => !v.isInstalled) ?? [];

  const consumerVendors = getConsumerVendors(sortedVendors, dataMap).filter((v) => {
    // Filter out vendors whose data deletability is "No - data must be retained"
    let deleteInstruction = v.instructions.find((i) => i.requestType == "DELETE");
    if (!deleteInstruction) {
      return false;
    }
    if (
      v.dataDeletability === "DELETABLE" &&
      deleteInstruction.hasRetentionExceptions === false &&
      deleteInstruction.processingMethod === "RETAIN"
    ) {
      return false;
    }
    return true;
  });

  const vendorsToContact = eligibleVendorsToContactForDeleteRequest(
    consumerVendors,
    /* request = */ null,
    dataMap,
    managedAdNetworks,
  )[0];
  const vendorIdsToContact = vendorsToContact.map((v) => v.vendorId);

  // With POL-6994, we also want to allow overriding DNC for internal storage locations, so they can be contacted
  const storageLocations = getConsumerVendors(sortedVendors, dataMap).filter(
    (v) => v.dataRecipientType === "storage_location",
  );

  // Add vendors from `storageLocations` whose delete instruction `doNotContact` is set to `false`
  storageLocations.forEach((location) => {
    const instruction = instructions?.find((i) => i.vendorId === location.vendorId);
    if (
      instruction &&
      instruction.doNotContact === false &&
      !vendorIdsToContact.includes(location.vendorId)
    ) {
      vendorIdsToContact.push(location.vendorId);
    }
  });

  const vendorsToDeleteInfo = vendorsForDeleteInfoStep(
    allVendors,
    /* request = */ null,
    dataMap,
    sellingAndSharingAnswers,
    managedAdNetworks,
    organization,
  ).filter(canVendorHaveDataDeleted);
  let vendorIdsToDeleteInfo = vendorsToDeleteInfo.map((v) => v.vendorId);

  const [vendorsState, setVendorsState] = useState<OrganizationDataRecipientDto[] | null>(null);

  // Initialize vendorsState
  useEffect(() => {
    if (!vendors || vendors.length == 0 || !dataMap || !instructions) {
      return;
    }
    if (vendorsState === null) {
      setVendorsState(vendors);
    }
  }, [dataMap, instructions, vendors, vendorsState]);

  // UI state

  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  const [showStorageLocationsModal, setShowStorageLocationsModal] = useState(false);

  // Data fetching and state for automation and email templates

  const [
    autoSendRequestEmailNotificationsForDeleteRequestsValue,
    setAutoSendRequestEmailNotificationsForDeleteRequestsValue,
  ] = useState(null);

  useEffect(() => {
    if (organization) {
      setAutoSendRequestEmailNotificationsForDeleteRequestsValue(
        organization.autoSendRequestEmailNotificationsForDeleteRequests,
      );
    }
  }, [organization]);

  const { result: consumerRequestClosedDeleteSampleMessage, refresh: refreshConsumerMessage } =
    useDataRequest({
      queryKey: ["emailTemplate", organization?.id, "closed-delete"],
      api: () => Api.organization.getOrganizationMessageSample(organization?.id, "closed-delete"),
    });

  const showCustomTextInputField =
    consumerRequestClosedDeleteSampleMessage?.templateHTML?.includes("[[[CUSTOM_TEXT]]]");

  const { result: vendorMessageTemplatePreview } = useDataRequest({
    queryKey: ["vendorMessageTemplatePreview", organization?.id, "delete"],
    api: () =>
      Api.dataRequest.getRequestContactVendorsPreviewMessageTemplateForRequestType(
        organization?.id,
        "delete",
      ),
  });

  // API update hooks

  const { fetch: updateDataRecipientApi } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(organization?.id, vendorId, vendorSettings);
    },
    onSuccess: fetchVendors,
  });

  const { fetch: updateInstructionApi } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(organization?.id, newInstruction);
    },
    onSuccess: fetchInstructions,
  });

  const { fetch: toggleShopifyInstallApi } = useActionRequest({
    api: async ({ appId, included }: { appId: string; included: boolean }) => {
      if (included) {
        return await Api.organizationVendor.addPlatformApplication(
          organization?.id,
          SHOPIFY_APP_VENDOR_ID,
          appId,
        );
      } else {
        return await Api.organizationVendor.removePlatformApplication(
          organization?.id,
          SHOPIFY_APP_VENDOR_ID,
          appId,
        );
      }
    },
    onSuccess: () => refreshShopifyPlatformApps(),
  });

  if (
    !NetworkRequest.areFinished(instructionsRequest, orgRequest, vendorsRequest, dataMapRequest) ||
    showShopifyColumn === undefined ||
    vendorsState === null
  ) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  // Update handler functions (that optimistically updates both local state and API simultaneously)

  const updateDataRecipient = async (updateVendorArgs: UpdateVendorArgs) => {
    setVendorsState(
      vendorsState.map((vendor) => {
        if (vendor.vendorId === updateVendorArgs.vendorId) {
          return {
            ...vendor,
            ...updateVendorArgs.vendorSettings,
          };
        }
        return vendor;
      }),
    );

    await updateDataRecipientApi(updateVendorArgs);
  };

  const updateInstruction = async (newInstruction: RequestHandlingInstructionDto) => {
    setInstructions(
      instructions.map((instruction) => {
        if (instruction.id === newInstruction.id) {
          return {
            ...instruction,
            ...newInstruction,
          };
        }
        return instruction;
      }),
    );

    await updateInstructionApi(newInstruction);
  };

  const toggleShopifyInstall = async ({
    appId,
    included,
  }: {
    appId: string;
    included: boolean;
  }) => {
    if (included) {
      setShopifyInstalledVendorIds((existing) => [...existing, appId]);
    } else {
      setShopifyInstalledVendorIds((existing) => existing.filter((id) => id !== appId));
    }

    await toggleShopifyInstallApi({ appId, included });
  };

  let missingContactEmailVendorNames = vendorsState
    .filter((v) => {
      let deleteInstruction = instructions?.find(
        (i) => i.vendorId == v.vendorId && i.requestType == "DELETE",
      );
      // If for some reason there is no delete instruction, then this vendor won't show up in the table, so just skip it by marking it as complete
      if (!deleteInstruction) return false;
      return !isRowContactEmailCompleted(v, deleteInstruction);
    })
    .map((v) => v.name);
  let isAutomationToggleDisabled = missingContactEmailVendorNames.length > 0;
  let isAutomationToggleDisabledTooltipText = `Missing contact email for: ${missingContactEmailVendorNames.join(
    ", ",
  )}`;

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="request-to-delete"
      nextOnClick={async () => {
        history.push(Routes.organization.requestHandling.requestToOptOut);
      }}
      nextDisabled={false}
      hideFooter={sidebarOpenForVendorId != null}
    >
      {organization && (
        <>
          <div className="tw-boundary">
            <div className="max-w-[950px] ml-[40px]">
              <ExplanatoryCard
                topBody={
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                      What is a Request to Delete?
                    </h3>
                    <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                      <p>
                        A consumer's request that their personal data stored by your business be
                        permanently deleted or anonymized from its stored records. Your business is
                        also required to "flow down" - or notify - third parties to whom you may
                        have sent the consumer's data that they have requested deletion. Data can be
                        retained under approved exceptions to deletion in{" "}
                        <a
                          href="/workflows/data-storage-settings"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-sm leading-5 underline text-primary-600"
                        >
                          Data Storage settings
                        </a>
                        .
                      </p>
                      <p>
                        Your deletion workflow will be automatically configured based on your inputs
                        in the table below.
                      </p>
                    </div>
                  </div>
                }
                expandable={false}
              />
            </div>

            <div className="flex items-center gap-3 mt-10">
              <StepNumberCircle text="1" />
              <div className="text-neutral-900 text-[18px] font-normal">
                Review your systems with accessible data that you will delete in your Request to
                Delete workflow.
              </div>
            </div>

            <DataRecipientsTable
              vendors={vendorsState.filter((v) => vendorIdsToDeleteInfo.includes(v.vendorId))}
              instructions={instructions}
              showContactForRequestsColumn={false}
              apiIntegrations={apiIntegrations}
              shopifyInstalledVendorIds={shopifyInstalledVendorIds}
              showShopifyColumn={showShopifyColumn}
              sidebarOpenForVendorId={sidebarOpenForVendorId}
              setSidebarOpenForVendorId={setSidebarOpenForVendorId}
              managedAdNetworks={managedAdNetworks}
              allVendors={allVendors}
              customAudienceVendorManagers={customAudienceVendorManagers}
            />

            <div className="mt-2.5 ml-[40px] text-neutral-600 text-[14px] font-medium">
              Update your data deletion settings in{" "}
              <a
                href={Routes.workflows.dataStorageSettings}
                className="text-blue-600 underline font-medium"
              >
                Data Storage
              </a>
            </div>

            <div className="flex items-center gap-3 mt-10">
              <StepNumberCircle text="2" />
              <div className="text-neutral-900 text-[18px] font-normal">
                Review your vendors who will be contacted for data deletion via "flow-down" email.
              </div>
            </div>

            <DataRecipientsTable
              vendors={vendorsState.filter((v) => vendorIdsToContact.includes(v.vendorId))}
              instructions={instructions}
              showDeletionInstructionsColumn={false}
              apiIntegrations={apiIntegrations}
              shopifyInstalledVendorIds={shopifyInstalledVendorIds}
              showShopifyColumn={showShopifyColumn}
              sidebarOpenForVendorId={sidebarOpenForVendorId}
              setSidebarOpenForVendorId={setSidebarOpenForVendorId}
              managedAdNetworks={managedAdNetworks}
              allVendors={allVendors}
              customAudienceVendorManagers={customAudienceVendorManagers}
            />

            <div className="mt-2.5 ml-[40px]">
              <Button color="tertiary" onClick={() => setShowStorageLocationsModal(true)}>
                Add Internal Storage Location
              </Button>
            </div>

            {showStorageLocationsModal && (
              <InternalStorageLocationsModal
                onClose={() => setShowStorageLocationsModal(false)}
                storageLocations={storageLocations}
                instructions={instructions}
                updateInstruction={updateInstruction}
              />
            )}

            {sidebarOpenForVendorId && (
              <DataRecipientSidebar
                dataRecipient={vendorsState.find((v) => v.vendorId === sidebarOpenForVendorId)}
                deleteInstruction={instructions.find(
                  (i) => i.vendorId === sidebarOpenForVendorId && i.requestType == "DELETE",
                )}
                updateDataRecipient={updateDataRecipient}
                updateInstruction={updateInstruction}
                showShopifyColumn={showShopifyColumn}
                isShopifyInstalled={shopifyInstalledVendorIds?.includes(sidebarOpenForVendorId)}
                toggleShopifyInstall={toggleShopifyInstall}
                apiIntegrations={apiIntegrations}
                isSidebarOpen={sidebarOpenForVendorId != null}
                closeSidebar={() => setSidebarOpenForVendorId(null)}
                openSidebar={() => {}}
              />
            )}

            <div className="mt-2.5 ml-[40px] text-neutral-600 text-[14px] font-medium">
              Update your data deletion settings in{" "}
              <a
                href={Routes.workflows.dataStorageSettings}
                className="text-blue-600 underline font-medium"
              >
                Data Storage
              </a>
            </div>

            <div className="flex items-center gap-3 mt-10">
              <StepNumberCircle text="3" />
              <div className="text-gray-900 text-[18px] font-normal">
                Auto-send request processing emails
              </div>
              <ToolTip
                text={isAutomationToggleDisabled ? isAutomationToggleDisabledTooltipText : null}
              >
                <Toggle
                  // Even if the automation setting is set to ON, if it becomes disabled (because of e.g. a new vendor was added with a missing contact email), then the automation won't work anyways, so show the automation toggle as OFF whenever it's disabled
                  checked={
                    isAutomationToggleDisabled
                      ? false
                      : autoSendRequestEmailNotificationsForDeleteRequestsValue
                  }
                  disabled={isAutomationToggleDisabled}
                  onChange={async (checked) => {
                    setAutoSendRequestEmailNotificationsForDeleteRequestsValue(checked);
                    await Api.organization.updateOrganization(organization.id, {
                      name: organization.name,
                      logoUrl: organization.logoUrl,
                      regulations: [],
                      autoSendRequestEmailNotificationsForDeleteRequests: checked,
                    });
                    await refreshOrg();

                    if (checked) {
                      snackbar(
                        "Enabled automatically sending deletion request email notifications",
                        {
                          variant: "success",
                        },
                      );
                    } else {
                      // closeSnackbar functionality not available in new API
                    }
                  }}
                />
              </ToolTip>
            </div>
            <div className="text-neutral-900 font-normal text-[14px] max-w-[950px] ml-[40px] mt-[6px]">
              If this is toggled off, you will be prompted to manually click a button to send the
              vendor email and consumer notification email below for each request you process.
            </div>
          </div>

          {/* Put the email templates outside of tw-boundary since the template HTML will only render correctly with default styles, and will not render correctly if overriden with tailwind styles */}
          <div style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "700px" }}>
            <div className="tw-boundary">
              <div className="text-neutral-900 text-[14px]">Vendor Email Notification Template</div>
            </div>

            <div
              style={{
                backgroundColor: "#F9F8FB",
                padding: "32px",
                maxWidth: "736px",
                marginTop: "6px",
              }}
            >
              <p>
                <strong>To:</strong> <span>[recipient email]</span>
              </p>

              <p>
                <strong>Subject: CCPA Request to Delete</strong>
              </p>

              <div dangerouslySetInnerHTML={{ __html: vendorMessageTemplatePreview }}></div>
            </div>

            <div className="tw-boundary">
              <div className="text-neutral-900 text-sm mt-3">
                Consumer Email Notification Template
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#F9F8FB",
                padding: "32px",
                maxWidth: "736px",
                marginTop: "6px",
              }}
            >
              <p>
                <strong>To:</strong> <span>consumer@example.com</span>
              </p>

              <p>
                <strong>Subject: A message about your request [REQUEST ID#]</strong>
              </p>

              {showCustomTextInputField ? (
                <EmailTemplateWithCustomTextInputField
                  orgId={organization.id}
                  consumerRequestSampleMessage={consumerRequestClosedDeleteSampleMessage}
                  refreshConsumerMessage={refreshConsumerMessage}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: consumerRequestClosedDeleteSampleMessage?.templateHTML,
                  }}
                ></div>
              )}
            </div>
          </div>
        </>
      )}
    </WorkflowSetupPageWrapper>
  );
};

function DataRecipientsTable({
  vendors,
  instructions,
  showContactForRequestsColumn = true,
  showDeletionInstructionsColumn = true,
  apiIntegrations,
  shopifyInstalledVendorIds,
  showShopifyColumn,
  sidebarOpenForVendorId,
  setSidebarOpenForVendorId,
  managedAdNetworks,
  allVendors,
  customAudienceVendorManagers,
}: {
  vendors: OrganizationDataRecipientDto[];
  instructions: RequestHandlingInstructionDto[];
  showContactForRequestsColumn?: boolean;
  showDeletionInstructionsColumn?: boolean;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  shopifyInstalledVendorIds: string[];
  showShopifyColumn: boolean;
  sidebarOpenForVendorId: string | null;
  setSidebarOpenForVendorId: React.Dispatch<React.SetStateAction<string | null>>;
  managedAdNetworks?: OrganizationManagedAdNetworkDto[];
  allVendors?: OrganizationDataRecipientDto[];
  customAudienceVendorManagers?: string[];
}) {
  let vendorNameColWidth = showShopifyColumn ? 300 : 270;
  let contactForRequestsColWidth = showContactForRequestsColumn
    ? showShopifyColumn
      ? 188
      : 203
    : 0;
  let deletionInstructionsColWidth = showDeletionInstructionsColumn
    ? showShopifyColumn
      ? 188
      : 203
    : 0;
  let shopifyColWidth = showShopifyColumn ? 214 : 0;
  let openSidebarColWidth = 60;

  let totalTableWidth =
    vendorNameColWidth +
    contactForRequestsColWidth +
    deletionInstructionsColWidth +
    shopifyColWidth +
    openSidebarColWidth;

  return (
    <div
      className={`border border-solid border-gray-200 rounded-lg mt-1.5 ml-[40px] max-h-[636px] overflow-y-auto relative`}
      style={{
        maxWidth: `${totalTableWidth}px`,
      }}
    >
      <table>
        <colgroup>
          <col style={{ width: `${vendorNameColWidth}px` }} />
          {showContactForRequestsColumn && (
            <col style={{ width: `${contactForRequestsColWidth}px` }} />
          )}
          {showDeletionInstructionsColumn && (
            <col style={{ width: `${deletionInstructionsColWidth}px` }} />
          )}
          {showShopifyColumn && <col style={{ width: `${shopifyColWidth}px` }} />}
          <col style={{ width: `${openSidebarColWidth}px` }} />
        </colgroup>
      </table>
      <thead className="sticky top-0 z-10">
        <tr>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tl-lg pl-6 text-left text-xs font-medium text-gray-500 uppercase"
          >
            Vendor Name
          </th>
          {showContactForRequestsColumn && (
            <th
              scope="col"
              className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-center text-xs font-medium text-gray-500 uppercase"
            >
              Contact for Requests
            </th>
          )}
          {showDeletionInstructionsColumn && (
            <th
              scope="col"
              className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-center text-xs font-medium text-gray-500 uppercase"
            >
              Deletion Instructions
            </th>
          )}
          {showShopifyColumn && (
            <th
              scope="col"
              className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-center text-xs font-medium text-gray-500 uppercase"
            >
              Installed on Shopify
            </th>
          )}
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tr-lg text-center text-xs font-medium text-gray-500 uppercase"
          >
            &nbsp;
          </th>
        </tr>
      </thead>
      <tbody>
        {vendors.map((dataRecipient) => {
          let deleteInstruction = instructions?.find(
            (i) => i.vendorId == dataRecipient.vendorId && i.requestType == "DELETE",
          );

          if (!deleteInstruction) {
            return null;
          }

          return (
            <DataRecipientRow
              key={dataRecipient.id}
              dataRecipient={dataRecipient}
              deleteInstruction={deleteInstruction}
              showContactForRequestsColumn={showContactForRequestsColumn}
              showDeletionInstructionsColumn={showDeletionInstructionsColumn}
              showShopifyColumn={showShopifyColumn}
              isShopifyInstalled={shopifyInstalledVendorIds?.includes(dataRecipient.vendorId)}
              apiIntegrations={apiIntegrations}
              openSidebar={() => setSidebarOpenForVendorId(dataRecipient.vendorId)}
              isSidebarOpen={sidebarOpenForVendorId === dataRecipient.vendorId}
              managedAdNetworks={managedAdNetworks}
              allVendors={allVendors}
              customAudienceVendorManagers={customAudienceVendorManagers}
            />
          );
        })}
      </tbody>
    </div>
  );
}

function DataRecipientRow({
  dataRecipient,
  deleteInstruction,
  showContactForRequestsColumn = true,
  showDeletionInstructionsColumn = true,
  showShopifyColumn,
  isShopifyInstalled,
  apiIntegrations,
  isSidebarOpen,
  openSidebar,
  managedAdNetworks,
  allVendors,
  customAudienceVendorManagers,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  deleteInstruction: RequestHandlingInstructionDto;
  showContactForRequestsColumn?: boolean;
  showDeletionInstructionsColumn?: boolean;
  showShopifyColumn: boolean;
  isShopifyInstalled: boolean;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  managedAdNetworks?: OrganizationManagedAdNetworkDto[];
  allVendors?: OrganizationDataRecipientDto[];
  customAudienceVendorManagers?: string[];
}) {
  const allowEditingInstalledApp =
    !dataRecipient.isPlatform &&
    !dataRecipient.isStandalone &&
    !isThirdPartyRecipient(dataRecipient);

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr
        key={dataRecipient.id}
        className={clsx("border-t border-solid border-gray-200", "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        <td
          className={clsx(
            `px-6 py-3.5 border-b border-solid border-gray-200`,
            showShopifyColumn ? "w-[300px]" : "w-[270px]",
          )}
        >
          <div className="flex items-center gap-3">
            <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-6 w-6" />
            <span className="text-[#111827] font-medium text-sm">{dataRecipient.name}</span>
            {vendorApiConnection &&
              (vendorApiConnection.connected ? (
                <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
              ) : (
                <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
              ))}
          </div>
          {(() => {
            if (!managedAdNetworks || !allVendors || !customAudienceVendorManagers) {
              return null;
            }

            // Show which networks this vendor manages (only if they are an active manager)
            const networksManaged =
              managedAdNetworks
                ?.filter(
                  (item) =>
                    item.managerId === dataRecipient.id &&
                    customAudienceVendorManagers?.includes(item.managerId),
                )
                ?.map((item) => allVendors?.find((v) => v.id === item.networkId))
                ?.filter(Boolean) || [];

            if (networksManaged.length === 0) return null;

            return (
              <div className="mt-2">
                <div className="text-gray-600 text-xs font-medium">
                  Managing: {networksManaged.map((network) => network?.name).join(", ")}
                </div>
              </div>
            );
          })()}
        </td>
        {showContactForRequestsColumn && (
          <td
            className={clsx(
              `text-center border-b border-solid border-gray-200`,
              showShopifyColumn ? "w-[188px]" : "w-[203px]",
            )}
          >
            {contactForRequestsColumnValue(dataRecipient, deleteInstruction)}
          </td>
        )}
        {showDeletionInstructionsColumn && (
          <td
            className={clsx(
              `text-center border-b border-solid border-gray-200`,
              showShopifyColumn ? "w-[188px]" : "w-[203px]",
            )}
          >
            {deletionInstructionsColumnValue(dataRecipient, deleteInstruction)}
          </td>
        )}
        {showShopifyColumn && (
          <td className="text-center w-[214px] max-w-[214px] border-b border-solid border-gray-200">
            {allowEditingInstalledApp ? (
              <div
                className={clsx(
                  "inline-block px-2.5 py-0.5 font-medium text-sm rounded-full text-center",
                  isShopifyInstalled
                    ? "text-[#065F46] bg-[#D1FAE5]"
                    : "text-[#991B1B] bg-[#FEE2E2]",
                )}
              >
                {isShopifyInstalled ? "Yes" : "No"}
              </div>
            ) : (
              <hr className="w-[51px] text-neutral-700 mx-auto" />
            )}
          </td>
        )}
        <td className="w-[60px] max-w-[60px] border-b border-solid border-gray-200">
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>
    </>
  );
}

function DataRecipientSidebar({
  dataRecipient,
  deleteInstruction,
  updateDataRecipient,
  updateInstruction,
  showShopifyColumn,
  isShopifyInstalled,
  toggleShopifyInstall,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  deleteInstruction: RequestHandlingInstructionDto;
  updateDataRecipient: (dataRecipient: UpdateVendorArgs) => void;
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  showContactForRequestsColumn?: boolean;
  showDeletionInstructionsColumn?: boolean;
  showShopifyColumn: boolean;
  isShopifyInstalled: boolean;
  toggleShopifyInstall: ({ appId, included }: { appId: string; included: boolean }) => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
}) {
  let email = dataRecipient.email || dataRecipient.defaultEmail;
  let doNotContactValue = deleteInstruction.doNotContact;
  let doNotContactReason = deleteInstruction.doNotContactReason;
  let doNotContactExplanation = deleteInstruction.doNotContactExplanation;
  let userProvidedDeletionInstruction = deleteInstruction.processingInstructions;

  // For some vendors, we want to override the "Do Not Contact" to always be true (i.e. we never want to contact this vendor), see POL-4154
  let shouldNeverContact = shouldNotContactForDelete(dataRecipient, deleteInstruction);

  let hasVendorDeletionInstructions = dataRecipient.deletionInstructions?.length > 0;

  let disableAddCustomDeletionInstructions =
    dataRecipient.dataAccessibility === "NOT_ACCESSIBLE" ||
    dataRecipient.dataDeletability === "NOT_DELETABLE";

  const allowEditingInstalledApp =
    !dataRecipient.isPlatform &&
    !dataRecipient.isStandalone &&
    !isThirdPartyRecipient(dataRecipient);

  let doNotContactCheckbox = (
    <SimpleCheckbox
      name="doNotContact"
      label={`Do Not Contact ${dataRecipient.name} for Deletion Requests`}
      initialChecked={shouldNeverContact ? true : deleteInstruction?.doNotContact}
      allowInteraction={!shouldNeverContact}
      onChange={async () => {
        let newDoNotContact = !doNotContactValue;

        let updatedInstruction = {
          ...deleteInstruction,
          doNotContact: newDoNotContact,
        };

        await updateInstruction(updatedInstruction);
      }}
    />
  );

  let addCustomDeletionInstructions = (
    <div
      className={clsx(
        "mt-4",
        disableAddCustomDeletionInstructions && "opacity-50 cursor-not-allowed",
      )}
    >
      <div className="text-neutral-700 text-[14px] font-medium">
        {hasVendorDeletionInstructions
          ? "Add Your Deletion Instructions (optional)"
          : "Add Your Deletion Instructions"}
      </div>
      <textarea
        rows={4}
        className="block w-full rounded-md bg-white px-3 py-1.5 text-[14px] text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
        defaultValue={userProvidedDeletionInstruction}
        placeholder="E.g. find the consumer first at the settings.."
        disabled={disableAddCustomDeletionInstructions}
        onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
          let newUserProvidedDeletionInstruction = e.target.value;

          let updatedInstruction = {
            ...deleteInstruction,
            processingInstructions: newUserProvidedDeletionInstruction,
          };

          await updateInstruction(updatedInstruction);
        }}
      />
    </div>
  );

  return (
    <Slideout
      open={isSidebarOpen}
      setOpen={(newIsOpen: boolean) => {
        if (newIsOpen) {
          openSidebar();
        } else {
          closeSidebar();
        }
      }}
      slideFrom="right"
    >
      <>
        <div className="bg-neutral-0 p-6">
          <div className="flex justify-between items-center">
            <h3 className="text-gray-900 text-[18px] font-bold">
              Request to Delete Vendor Settings
            </h3>
            <div className="cursor-pointer" onClick={closeSidebar}>
              <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
            </div>
          </div>

          <div className="mt-1">
            <div className="flex items-center">
              <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-5 w-5" />
              <span className="text-gray-900 font-medium text-[18px] ml-3">
                {dataRecipient.name}
              </span>
            </div>
            {dataRecipient.url?.length > 0 && (
              <div className="mt-1 text-[14px] font-normal text-gray-500">{dataRecipient.url}</div>
            )}
          </div>
        </div>
        <div>
          <Form
            onSubmit={() => {}}
            render={() => {
              return (
                <>
                  <div className="p-6">
                    {showShopifyColumn && allowEditingInstalledApp && (
                      <>
                        <div className="flex justify-between items-start pb-6">
                          <div>
                            <label className="block text-[14px] font-medium text-gray-700">
                              Installed on Shopify
                            </label>
                            <div className="mt-1 text-[12px] leading-4 text-gray-500">
                              If you or TrueVault have confirmed that this app deletes data from
                              Shopify's instructions, then we will remove it from the deletion
                              workflow. Otherwise, it will remain in your workflow until it is
                              confirmed to follow Shopify's instructions.
                            </div>
                          </div>
                          <RadioGroup
                            name="installedOnShopify"
                            direction="horizontal"
                            options={[
                              {
                                label: `Yes`,
                                value: "YES",
                              },
                              {
                                label: `No`,
                                value: "NO",
                              },
                            ]}
                            selectedValue={isShopifyInstalled ? "YES" : "NO"}
                            onChange={async (newValue: string) => {
                              let newShopifyInstalledValue = newValue === "YES";

                              await toggleShopifyInstall({
                                appId: dataRecipient.vendorId,
                                included: newShopifyInstalledValue,
                              });
                            }}
                          />
                        </div>
                        <hr className="border-gray-200" />
                      </>
                    )}
                    <div className={`${allowEditingInstalledApp ? "mt-6" : ""}`}>
                      <label className="block text-[14px] font-medium text-gray-700">
                        Contact Email for Requests
                      </label>
                      <TextInput
                        field="vendor-delete-contact-email"
                        onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                          let newEmail = e.target.value;

                          let updatedDataRecipient = {
                            ...dataRecipient,
                            email: newEmail,
                          };

                          await updateDataRecipient({
                            vendorId: dataRecipient.vendorId,
                            vendorSettings: {
                              ...updatedDataRecipient,
                              classificationId: dataRecipient.classification.id,
                            },
                          });
                        }}
                        defaultValue={email}
                        maxLength={90}
                        disabled={doNotContactValue || shouldNeverContact}
                      />
                    </div>
                    {/* With POL-6994 we want to allow adding storage locations to the contact workflow, which is configured via a separate modal, so don't allow adjusting `doNotContact` for storage locations */}
                    {dataRecipient.dataRecipientType !== "storage_location" && (
                      <div className="mt-6">
                        {shouldNeverContact ? (
                          <ToolTip
                            text="This vendor will never be contacted for deletion requests as it is a third-party vendor that is not marked for either selling or sharing."
                            yOffset={85}
                          >
                            {doNotContactCheckbox}
                          </ToolTip>
                        ) : (
                          doNotContactCheckbox
                        )}

                        {!shouldNeverContact && (
                          <div className="mt-4 pl-8">
                            <RadioGroup
                              name="doNotContactReasons"
                              disabled={!doNotContactValue && !shouldNeverContact}
                              options={[
                                {
                                  label: `We will notify ${dataRecipient.name} of the consumer's deletion request through other means (e.g., webform, custom API). Email contact is not necessary.`,
                                  value: DoNotContactReason.CONTACT_VIA_OTHER_MEANS,
                                },
                                {
                                  label: `It is impossible or extremely difficult to contact this Data Recipient.`,
                                  value: DoNotContactReason.IMPOSSIBLE_OR_DIFFICULT,
                                },
                              ]}
                              selectedValue={doNotContactReason}
                              onChange={async (newValue: string) => {
                                let updatedInstruction = {
                                  ...deleteInstruction,
                                  doNotContactReason: newValue as DoNotContactReason,
                                };

                                await updateInstruction(updatedInstruction);
                              }}
                            />

                            {doNotContactReason == DoNotContactReason.IMPOSSIBLE_OR_DIFFICULT && (
                              <div className="pl-[33px] mt-5">
                                <div className="text-[14px] text-neutral-700 font-medium mb-1">
                                  Describe why. This text will be included with your response to
                                  consumer deletion requests.
                                </div>
                                <textarea
                                  rows={4}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                  defaultValue={
                                    doNotContactExplanation && doNotContactExplanation.length > 0
                                      ? doNotContactExplanation
                                      : null
                                  }
                                  placeholder="E.g. they are no longer in business"
                                  onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                                    let newExplanation = e.target.value;

                                    let updatedInstruction = {
                                      ...deleteInstruction,
                                      doNotContactExplanation: newExplanation,
                                    };

                                    await updateInstruction(updatedInstruction);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <hr className="border-gray-200" />
                  {dataRecipient.dataRecipientType !== "storage_location" && (
                    <div className="p-6">
                      <div className="flex justify-between items-center">
                        <div className="text-[14px] text-gray-700 font-medium">
                          Self-Service Deletion Instructions
                        </div>
                        {hasVendorDeletionInstructions && (
                          <div className="flex items-center gap-1">
                            {TRUEVAULT_BADGE_ICON}
                            <span className="text-primary-600 text-[10px] font-medium">
                              Verified
                            </span>
                          </div>
                        )}
                      </div>
                      {hasVendorDeletionInstructions && (
                        <div className="vendor-instructions mt-3">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: dataRecipient.deletionInstructions,
                            }}
                          />
                        </div>
                      )}
                      {disableAddCustomDeletionInstructions ? (
                        <ToolTip
                          text="Not applicable as this vendor's data is marked inaccessible"
                          yOffset={85}
                        >
                          {addCustomDeletionInstructions}
                        </ToolTip>
                      ) : (
                        addCustomDeletionInstructions
                      )}
                    </div>
                  )}
                </>
              );
            }}
          />
        </div>
      </>
    </Slideout>
  );
}

function InternalStorageLocationsModal({
  onClose,
  storageLocations,
  instructions,
  updateInstruction,
}: {
  onClose: () => void;
  storageLocations: OrganizationDataRecipientDto[];
  instructions: RequestHandlingInstructionDto[];
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
}) {
  return (
    <Modal
      title="Add Internal Data Storage"
      maxPanelWidth="max-w-[600px]"
      firstButton="Done"
      alignButton="right"
      onFirstButtonClick={onClose}
      showOverlay
      closeButton
      onClose={onClose}
    >
      <div className="text-gray-500 text-[14px]">
        Select internal storage locations from below to add to your data deletion workflow.
      </div>
      {storageLocations.map((location) => {
        const instruction = instructions.find((i) => i.vendorId === location.vendorId);
        // With POL-6994, we "add" the storage location to the delete contact workflow by setting its `doNotContact` to false (since by default, internal storage locations delete instructions have `doNotContact` set to null)
        const isInDeleteContactWorkflow = instruction?.doNotContact === false;

        return (
          <div
            key={location.id}
            className={clsx(
              "p-[16px] border border-neutral-300 rounded-[4px]",
              "flex items-center justify-between",
            )}
          >
            <div className="text-[14px] text-black">{location.name}</div>
            <div>
              <Button
                color={isInDeleteContactWorkflow ? "tertiary" : "primary"}
                onClick={async () => {
                  let newInstruction = {
                    ...instruction,
                    doNotContact: isInDeleteContactWorkflow ? null : false,
                  };

                  await updateInstruction(newInstruction);
                }}
              >
                {isInDeleteContactWorkflow ? "Remove from Workflow" : "Add to Workflow"}
              </Button>
            </div>
          </div>
        );
      })}
    </Modal>
  );
}

const contactForRequestsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  deleteInstruction: RequestHandlingInstructionDto,
): React.ReactNode => {
  let resultLabel = "",
    textColor = "",
    bgColor = "";
  let email = vendor.email || vendor.defaultEmail;

  if (shouldNotContactForDelete(vendor, deleteInstruction) || deleteInstruction?.doNotContact) {
    resultLabel = "Do Not Contact";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else if (email) {
    resultLabel = email;
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else {
    resultLabel = "Add Contact Email";
    textColor = "text-[#92400E]";
    bgColor = "bg-[#FEF3C7]";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 max-w-[188px] truncate font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};

export const TRUEVAULT_BADGE_ICON = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="6" fill="#5331C6" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.30508 6.06801C6.42869 5.97525 6.50865 5.82749 6.50865 5.66106C6.50865 5.38024 6.281 5.15259 6.00017 5.15259C5.71935 5.15259 5.4917 5.38024 5.4917 5.66106C5.4917 5.82734 5.57151 5.97497 5.69492 6.06775V6.47458C5.69492 6.64307 5.83151 6.77966 6 6.77966C6.16849 6.77966 6.30508 6.64307 6.30508 6.47458V6.06801Z"
      fill="white"
    />
    <path
      d="M8.27096 6.03392C8.27096 7.26954 7.2693 8.27121 6.03367 8.27121C4.79805 8.27121 3.79639 7.26954 3.79639 6.03392C3.79639 4.7983 4.79805 3.79663 6.03367 3.79663C7.2693 3.79663 8.27096 4.7983 8.27096 6.03392Z"
      stroke="white"
      strokeWidth="0.745763"
    />
    <path
      d="M6.57622 2.2373C6.57622 1.95648 6.34857 1.72883 6.06775 1.72883C5.78692 1.72883 5.55927 1.95648 5.55927 2.2373H6.57622ZM2.52554 7.49187C2.28234 7.63228 2.19901 7.94326 2.33943 8.18646C2.47984 8.42966 2.79081 8.51298 3.03401 8.37257L2.52554 7.49187ZM9.10147 8.37257C9.34467 8.51298 9.65565 8.42966 9.79606 8.18646C9.93647 7.94326 9.85314 7.63228 9.60994 7.49187L9.10147 8.37257ZM7.71199 7.57035L9.10147 8.37257L9.60994 7.49187L8.22046 6.68965L7.71199 7.57035ZM6.57622 3.83053V2.2373H5.55927V3.83053H6.57622ZM3.84758 6.72859L2.52554 7.49187L3.03401 8.37257L4.35606 7.60929L3.84758 6.72859Z"
      fill="white"
    />
  </svg>
);

const deletionInstructionsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  deleteInstruction: RequestHandlingInstructionDto,
): React.ReactNode => {
  let resultLabel: React.ReactNode = "",
    textColor = "",
    bgColor = "";

  if (
    vendor.dataAccessibility === "NOT_ACCESSIBLE" ||
    vendor.dataDeletability === "NOT_DELETABLE"
  ) {
    resultLabel = "Data Inaccessible";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else if (vendor?.deletionInstructions) {
    resultLabel = (
      <div className="flex items-center gap-1">
        {TRUEVAULT_BADGE_ICON}
        <span className="text-primary-600">Verified</span>
      </div>
    );
    bgColor = "bg-[#EDE9FE]";
  } else if (deleteInstruction?.processingInstructions?.trim()?.length > 1) {
    resultLabel = "Your Instructions";
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else {
    resultLabel = "Add Instructions";
    textColor = "text-[#92400E]";
    bgColor = "bg-[#FEF3C7]";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 max-w-[188px] truncate font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};

// eslint-disable-next-line unused-imports/no-unused-vars
const isRowCompleted = (
  dataRecipient: OrganizationDataRecipientDto,
  deleteInstruction: RequestHandlingInstructionDto,
): boolean => {
  if (shouldNotContactForDelete(dataRecipient, deleteInstruction)) {
    return true;
  }

  if (!isRowContactEmailCompleted(dataRecipient, deleteInstruction)) {
    return false;
  }

  // From here, we know that either the vendor has an email address, or they have set do not contact

  if (deleteInstruction.doNotContact && deleteInstruction.doNotContactReason == null) {
    return false;
  }

  // Now let's check deletion instructions

  // If data is inaccessible, then no deletion instructions required
  if (
    dataRecipient.dataAccessibility === "NOT_ACCESSIBLE" ||
    dataRecipient.dataDeletability === "NOT_DELETABLE"
  ) {
    return true;
  }

  // If there are official vendor deletion instructions, then no self-service deletion instructions required
  if (dataRecipient.deletionInstructions?.length > 0) {
    return true;
  }

  // At this point, we require self-service deletion instructions
  if (!(deleteInstruction.processingInstructions?.trim()?.length > 1)) {
    return false;
  }

  return true;
};

// Checks only the contact email column for row completion
const isRowContactEmailCompleted = (
  dataRecipient: OrganizationDataRecipientDto,
  deleteInstruction: RequestHandlingInstructionDto,
): boolean => {
  // First make sure we actually need to contact the vendor
  if (
    shouldNotContactForDelete(dataRecipient, deleteInstruction) ||
    deleteInstruction.doNotContact
  ) {
    return true;
  }

  // Check if we have either an email or a default email
  return Boolean(dataRecipient.email || dataRecipient.defaultEmail);
};

export function EmailTemplateWithCustomTextInputField({
  orgId,
  refreshConsumerMessage,
  consumerRequestSampleMessage,
}: {
  orgId: string;
  refreshConsumerMessage: RefreshFn;
  consumerRequestSampleMessage: CustomMessageSampleDto;
}) {
  const enqueueSnackbar = useSnackBar();

  const { fetch: updateCustomText } = useActionRequest({
    api: (customMessage) =>
      Api.organization.updateOrganizationMessageSample(
        orgId,
        "closed-delete",
        customMessage?.trim() ?? "",
      ),
  });

  const [customText, setCustomText] = useState(consumerRequestSampleMessage.customText);

  let { templateHTML } = consumerRequestSampleMessage;
  let [beforeHTML, afterHTML] = templateHTML
    .split(`<p class="custom-text">[[[CUSTOM_TEXT]]]</p>`)
    .map((t) => t.replace(/href/gi, "xhref"));

  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: beforeHTML }} />
      <div className="tw-boundary">
        <textarea
          rows={4}
          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          defaultValue={customText.length > 0 ? customText : null}
          placeholder="(optional) Add your own language here"
          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
            let newCustomText = e.target.value;

            setCustomText(newCustomText);

            await updateCustomText(newCustomText);
            await refreshConsumerMessage();

            enqueueSnackbar(`Custom text updated`, { variant: "success" });
          }}
        />
      </div>
      <div dangerouslySetInnerHTML={{ __html: afterHTML }} />
    </div>
  );
}
